import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAssignableDevices,
  fetchAssignableDevicesCount,
  fetchTargetCompanies,
  fetchTargetCompaniesCount,
  assignDevices,
  fetchAccessibleCompanies,
  removeDeviceAssignment,
} from '../api';
import { devicesKeys } from '@/features/devices/hooks';
import type {
  AssignableDeviceQueryParams,
  TargetCompanyQueryParams,
  ExistingAssignmentQueryParams,
  AssignDevicesRequest,
  RemoveDeviceAssignmentParams,
} from '../types';

export const deviceAssignmentKeys = {
  all: ['device-assignment'] as const,
  assignableDevices: () => [...deviceAssignmentKeys.all, 'assignable-devices'] as const,
  assignableDevicesList: (params: AssignableDeviceQueryParams) =>
    [...deviceAssignmentKeys.assignableDevices(), 'list', params] as const,
  assignableDevicesCount: (params: Omit<AssignableDeviceQueryParams, 'page' | 'pageSize'>) =>
    [...deviceAssignmentKeys.assignableDevices(), 'count', params] as const,
  targetCompanies: () => [...deviceAssignmentKeys.all, 'target-companies'] as const,
  targetCompaniesList: (params: TargetCompanyQueryParams) =>
    [...deviceAssignmentKeys.targetCompanies(), 'list', params] as const,
  targetCompaniesCount: (params: Omit<TargetCompanyQueryParams, 'page' | 'pageSize'>) =>
    [...deviceAssignmentKeys.targetCompanies(), 'count', params] as const,
  existingAssignments: () => [...deviceAssignmentKeys.all, 'existing-assignments'] as const,
  existingAssignmentsList: (params: ExistingAssignmentQueryParams) =>
    [...deviceAssignmentKeys.existingAssignments(), 'list', params] as const,
  existingAssignmentsCount: (companyId: string) =>
    [...deviceAssignmentKeys.existingAssignments(), 'count', companyId] as const,
  accessibleCompanies: (companyId: string) =>
    [...deviceAssignmentKeys.all, 'accessible-companies', companyId] as const,
};

export function useAssignableDevices(params: AssignableDeviceQueryParams, enabled = true) {
  return useQuery({
    queryKey: deviceAssignmentKeys.assignableDevicesList(params),
    queryFn: () => fetchAssignableDevices(params),
    enabled: enabled && !!params.companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useInvalidateAssignableDevices() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: deviceAssignmentKeys.assignableDevices() });
}

export function useAssignableDevicesCount(
  params: Omit<AssignableDeviceQueryParams, 'page' | 'pageSize'>,
  enabled = true
) {
  return useQuery({
    queryKey: deviceAssignmentKeys.assignableDevicesCount(params),
    queryFn: () => fetchAssignableDevicesCount(params),
    enabled: enabled && !!params.companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useTargetCompanies(params: TargetCompanyQueryParams, enabled = true) {
  return useQuery({
    queryKey: deviceAssignmentKeys.targetCompaniesList(params),
    queryFn: () => fetchTargetCompanies(params),
    enabled: enabled && !!params.sourceCompanyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useTargetCompaniesCount(
  params: Omit<TargetCompanyQueryParams, 'page' | 'pageSize'>,
  enabled = true
) {
  return useQuery({
    queryKey: deviceAssignmentKeys.targetCompaniesCount(params),
    queryFn: () => fetchTargetCompaniesCount(params),
    enabled: enabled && !!params.sourceCompanyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useInvalidateExistingAssignments() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: deviceAssignmentKeys.existingAssignments() });
}

/* export function useExistingAssignments(params: ExistingAssignmentQueryParams, enabled = true) {
  return useQuery({
    queryKey: deviceAssignmentKeys.existingAssignmentsList(params),
    queryFn: () => fetchExistingAssignments(params),
    enabled: enabled && !!params.companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}


export function useExistingAssignmentsCount(companyId: string, enabled = true) {
  return useQuery({
    queryKey: deviceAssignmentKeys.existingAssignmentsCount(companyId),
    queryFn: () => fetchExistingAssignmentsCount(companyId),
    enabled: enabled && !!companyId,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
} */

export function useAccessibleCompanies(companyId: string, enabled = true) {
  return useQuery({
    queryKey: deviceAssignmentKeys.accessibleCompanies(companyId),
    queryFn: () => fetchAccessibleCompanies(companyId),
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useAssignDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AssignDevicesRequest) => assignDevices(request),
    onSuccess: (_data, { targetCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: deviceAssignmentKeys.all });
      queryClient.invalidateQueries({ queryKey: devicesKeys.listsOfCompany(targetCompanyId) });
      queryClient.invalidateQueries({ queryKey: devicesKeys.countsOfCompany(targetCompanyId) });
    },
  });
}

// Shared remove action for every view that can take devices back from another company.
// It refreshes the assignment caches and only the two device lists the move affects,
// so other companies' lists mounted elsewhere (e.g. a drilled-into daughter company) don't refetch.
export function useRemoveDeviceAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceIds, ownCompanyId }: RemoveDeviceAssignmentParams) =>
      removeDeviceAssignment(deviceIds, ownCompanyId),
    onSuccess: (_data, { companyId, ownCompanyId }) => {
      queryClient.invalidateQueries({ queryKey: deviceAssignmentKeys.all });
      queryClient.invalidateQueries({ queryKey: devicesKeys.listsOfCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: devicesKeys.countsOfCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: devicesKeys.listsOfCompany(ownCompanyId) });
      queryClient.invalidateQueries({ queryKey: devicesKeys.countsOfCompany(ownCompanyId) });
    },
  });
}
